/**
 * Public terms of use. Google Play and the App Store both want a URL for this
 * next to the privacy policy, and a user-generated-content app is expected to
 * state its rules and how they are enforced — the store reviewer checks that the
 * rules exist and that the app can actually act on them.
 *
 * Same discipline as the privacy policy: every sentence here has to stay true of
 * the product. If a rule below is unenforceable in the app, either the app is
 * missing something or this page is lying.
 *
 * Russian original; the English version is TermsEn.tsx. Keep both in step.
 */
import { LegalDoc, List, Section } from '../LegalDoc';

const UPDATED = '5 августа 2026';

export function TermsRu() {
  return (
    <LegalDoc
      title="Условия использования"
      updated={UPDATED}
      sibling={{ href: '/privacy', label: 'Политика конфиденциальности' }}
    >
      <Section title="Коротко">
        <p>
          Бункер — мессенджер и голосовая связь площадки cispracs. Пользуясь им, ты соглашаешься с
          этими условиями. Если не согласен — не пользуйся: удалить аккаунт можно в любой момент.
        </p>
      </Section>

      <Section title="Аккаунт">
        <List
          items={[
            ['Вход через cispracs', 'отдельного пароля у Бункера нет; аккаунтом управляет площадка.'],
            ['Возраст', 'Бункер не предназначен для детей младше 13 лет.'],
            ['Один человек', 'аккаунт личный. За всё, что сделано под ним, отвечает владелец, поэтому не давай доступ другим.'],
            ['Достоверность', 'не выдавай себя за другого человека, команду или сотрудника площадки.'],
          ]}
        />
      </Section>

      <Section title="Что нельзя публиковать">
        <p className="mb-2">
          Всё, что ты отправляешь — текст, фото, файлы, голос, опросы, названия серверов и каналов, —
          твоя ответственность. Запрещено:
        </p>
        <List
          items={[
            ['Незаконное', 'то, что нарушает закон, включая материалы о сексуальной эксплуатации детей — за такое аккаунт удаляется без предупреждения, а материалы передаются по назначению.'],
            ['Травля и угрозы', 'преследование, угрозы, разжигание ненависти по любому признаку, доксинг — публикация чужих личных данных.'],
            ['Вред', 'вредоносное ПО, фишинг, ссылки на кражу аккаунтов, обман ради денег.'],
            ['Спам', 'массовая рассылка, накрутка, автоматизированные аккаунты и приглашения.'],
            ['Чужое', 'контент, права на который принадлежат другим, если у тебя нет разрешения.'],
            ['Обход ограничений', 'попытки обойти права, роли, блокировки, тайм-ауты и модерацию — включая обход техническими средствами.'],
          ]}
        />
      </Section>

      <Section title="Как это соблюдается">
        <p>
          На любое сообщение и любого пользователя можно пожаловаться прямо в приложении: меню
          сообщения → «Пожаловаться», карточка профиля → «Заблокировать». Жалобу разбирают модераторы
          сервера, а критичное уходит модерации площадки. Мы можем удалить контент, ограничить или
          удалить аккаунт, если правила нарушены. Решение можно оспорить, ответив на письмо от
          поддержки.
        </p>
      </Section>

      <Section title="Твои серверы">
        <p>
          Кто создал сервер — тот за него и отвечает: за его участников, роли, названия и то, что там
          происходит. Права владельца не отменяют этих условий.
        </p>
      </Section>

      <Section title="Права на контент">
        <p>
          Твой контент остаётся твоим. Отправляя его, ты разрешаешь нам хранить, передавать и
          показывать его тем, кому он адресован, — без этого мессенджер не работает. Мы не используем
          твой контент для рекламы и не передаём его третьим лицам ради выгоды.
        </p>
      </Section>

      <Section title="Сервис как есть">
        <p>
          Бункер предоставляется бесплатно и «как есть»: мы не обещаем бесперебойной работы, можем
          менять и отключать возможности, проводить обслуживание. Голос не записывается, а история
          сообщений не является резервной копией — важное храни у себя.
        </p>
      </Section>

      <Section title="Прекращение">
        <List
          items={[
            ['С твоей стороны', 'удали аккаунт — в приложении: профиль → «Удалить аккаунт».'],
            ['С нашей стороны', 'при нарушении этих условий или требований закона доступ может быть ограничен или прекращён.'],
          ]}
        />
      </Section>

      <Section title="Изменения и связь">
        <p>
          Если условия поменяются, здесь появится новая дата, а существенные изменения мы покажем в
          приложении. Вопросы —{' '}
          <a className="text-accent underline" href="mailto:support@cispracs.ru">
            support@cispracs.ru
          </a>
          .
        </p>
      </Section>
    </LegalDoc>
  );
}
